<template>
  <div :class="b('', { showMainImage })">
    <h3
      v-if="productSet.name"
      :class="b('name')"
    >
      {{ productSet.name }}
    </h3>
    <a
      :href="setUrl"
      :class="b('link')"
      :aria-label="mainImage?.alt || $t('c-product-set.link-alt-tag')"
    >
      <e-picture
        v-if="showMainImage"
        :srcset="mainImage?.srcset"
        :alt="mainImage?.alt || mainImage?.title || productSet.name"
        :title="mainImage?.title"
        :fallback="mainImage?.fallback"
        :ratio="1"
        :sizes="pictureSizes"
        :placeholder="!mainImage"
        loading="lazy"
        :class="b('image')"
        role="img"
      />
    </a>
    <div :class="b('articles-wrapper')">
      <div
        v-if="showMainImage"
        :class="b('articles-container-prefix')"
      >
        <e-icon
          icon="i-equal"
          size="20"
        />
      </div>

      <div
        ref="container"
        :class="b('articles-container', { isExpanded })"
      >
        <div
          v-for="(article, index) in productSetConfig.articles"
          :key="`article-${index}`"
          ref="articleItems"
          :class="b('article-item-container')"
        >
          <div
            v-if="index > 0"
            :class="b('article-item-prefix')"
          >
            <e-icon
              icon="i-plus"
              size="16"
            />
          </div>
          <div :class="b('article-item', { highlight: article.id === highlightId })">
            <e-picture
              :srcset="article.mainImage?.srcset"
              :alt="article.mainImage?.alt || article.mainImage?.title || article.name"
              :title="article.mainImage?.title"
              :fallback="article.mainImage?.fallback"
              :ratio="1"
              :sizes="pictureSizes"
              :placeholder="!article.mainImage"
              loading="lazy"
              :class="b('article-item-image')"
            />
            <span :class="b('article-item-label')">
              {{ article.name }}
            </span>
          </div>
        </div>
      </div>
      <button
        v-if="isMultiline"
        :class="b('button-show-more', { isExpanded, hide: !showToggleButton })"
        type="button"
        :aria-expanded="isExpanded"
        aria-controls="articles-container"
        @click="toggleExpanded"
      >
        {{ isExpanded ? $t('c-product-set.button-show-less') : $t('c-product-set.button-show-more') }}
        <e-icon
          :icon="isExpanded ? 'i-chevron--up' : 'i-chevron--down'"
          :size="isExpanded ? '14' : '18'"
        />
      </button>
    </div>
  </div>
</template>

<script lang="ts">
  import { PropType, Ref, defineComponent, ref } from 'vue';
  import { PRODUCT_DISPLAY_PICTURE_SIZES } from '@/setup/globals';
  import eIcon from '@/elements/e-icon.vue';
  import ePicture from '@/elements/e-picture.vue';
  import { HtmlElement } from '@/types/html';
  import { Image } from '@/types/image';
  import { ProductSet, ProductSetDetails } from '@/types/product';
  import { ImageSizes } from '@/types/sizes';

  type Setup = {
    container: Ref<HtmlElement>;
    articleItems: Ref<HtmlElement[] | null | undefined>;
  };

  type Data = {
    isMultiline: boolean;
    isExpanded: boolean;
    showToggleButton: boolean;
    pictureSizes: ImageSizes;
  };

  /**
   * Renders a product set and the included articles.
   */
  export default defineComponent({
    name: 'c-product-set',
    components: {
      eIcon,
      ePicture,
    },

    props: {
      /**
       * The product to be displayed.
       */
      productSetConfig: {
        type: Object as PropType<ProductSet>,
        required: true,
      },

      /**
       * The item with the given id will be highlighted.
       */
      highlightId: {
        type: Number,
        default: null,
      },

      /**
       * Whether to show the mainImage or not
       * We do not show it f.e. when using this component on a detail page
       */
      showMainImage: {
        type: Boolean,
        default: true,
      },
    },
    // emits: {},

    setup(): Setup {
      return {
        container: ref(),
        articleItems: ref(),
      };
    },
    data(): Data {
      return {
        isMultiline: false,
        isExpanded: false,
        showToggleButton: true,
        pictureSizes: PRODUCT_DISPLAY_PICTURE_SIZES,
      };
    },

    computed: {
      productSet(): ProductSetDetails {
        return this.productSetConfig.set;
      },

      mainImage(): Image | null {
        return this.productSet?.mainImage;
      },

      setUrl(): string {
        return this.productSet.url || '';
      },
    },
    // watch: {},

    // beforeCreate() {},
    // created() {},
    // beforeMount() {},
    mounted() {
      this.checkIfContainerIsMultiline();
      window.addEventListener('resizeend', this.checkIfContainerIsMultiline);
    },
    // beforeUpdate() {},
    // updated() {},
    // activated() {},
    // deactivated() {},
    beforeUnmount() {
      window.removeEventListener('resizeend', this.checkIfContainerIsMultiline);
    },
    // unmounted() {},

    methods: {
      toggleExpanded() {
        this.isExpanded = !this.isExpanded;
        this.showToggleButton = false;

        setTimeout(() => {
          this.showToggleButton = true;
        }, 175);
      },

      checkIfContainerIsMultiline() {
        if (!this.container || !this.articleItems?.length) {
          return;
        }

        this.isMultiline = this.container.scrollHeight >= (this.articleItems[0]?.offsetHeight || 0) * 2;
      },
    },
    // render() {},
  });
</script>

<style lang="scss">
  @use '@/setup/scss/variables';
  @use '@/setup/scss/mixins';
  @use '@/setup/scss/setup/headings';

  @mixin icon-divider(
    $size,
    $container-padding,
    $border-color,
    $background-color: variables.$va-color-grayscale--1000
  ) {
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    left: calc($size - $container-padding / 2) * -1;
    width: $size;
    height: $size;
    border: 4px solid $border-color;
    background-color: $background-color;
    border-radius: 100%;
  }

  .c-product-set {
    $this: &;
    $background-collection-item: variables.$va-color-grayscale--900;
    $container-padding: 1rem;

    display: grid;
    grid-template-areas:
      'name name'
      'options options';
    grid-template-columns: calc(var(--theme-image-size--lg) + 2rem) 1fr;
    gap: $container-padding;
    padding-bottom: 2rem;
    border-bottom: 1px solid variables.$va-color-grayscale--900;

    &--show-main-image {
      grid-template-areas:
        'image name'
        'options options';

      @include mixins.media(md) {
        grid-template-areas:
          'name name'
          'image options';
      }

      &__articles-container {
        width: 100%;
      }
    }

    &__name {
      @extend .headline-4;

      grid-area: name;
    }

    &__image {
      grid-area: image;
      height: 100%;
      width: calc(var(--theme-image-size--lg) + 2rem);
      background-color: var(--theme-color-grayscale--950);
      padding: $container-padding;
      display: flex;
      align-items: center;
    }

    &__articles-wrapper {
      grid-area: options;
      position: relative;
    }

    &__articles-container-prefix {
      @include icon-divider(2.2rem, $container-padding, var(--theme-color-grayscale--950));

      top: calc(50% - 1.1rem);
      z-index: 2;

      @include mixins.media($down: md) {
        left: -1rem;
        top: calc(100px - 1.1rem);
      }
    }

    &__articles-container {
      position: relative;
      background-color: var(--theme-color-grayscale--950);
      display: flex;
      flex-wrap: wrap;
      padding: $container-padding 2rem;
      gap: $container-padding;
      min-height: var(--theme-image-size--lg);
      max-height: calc(var(--theme-image-size--lg) + 2rem);
      overflow: hidden;
      transition: max-height 200ms ease-in-out;
      width: fit-content;
      z-index: 1;

      &--is-expanded {
        max-height: 100%;
      }
    }

    &__article-item-container {
      position: relative;
      display: flex;
      flex-wrap: wrap;
      align-items: stretch;
      border: 1px solid variables.$va-color-grayscale--800;
      background-color: $background-collection-item;
      padding: $container-padding;
      border-radius: 3px;
      gap: 2rem 0;
    }

    &__article-item-prefix {
      @include icon-divider(2.2rem, $container-padding, $background-collection-item);

      align-self: center;
    }

    &__article-item {
      position: relative;
      display: flex;
      flex-direction: column;
      width: 75px;
      height: 100%;
      min-height: 8rem;
      background-color: variables.$va-color-grayscale--1000;
      padding: 0.6rem;
      gap: 0.5rem;

      @include mixins.media(sm) {
        width: 6.5rem;
      }

      &:first-child {
        margin-left: 0;
      }

      &--highlight {
        border: 3px solid variables.$va-color-grayscale--0;
      }
    }

    &__article-item-label {
      @include mixins.line-clamp(3, 1.2);
      @include mixins.hyphens;

      font-size: variables.$font-size--smallest;
      line-height: 1.2;
      text-align: center;
    }

    &__button-show-more {
      $button-show-more-transition: 350ms ease-in-out;

      @include mixins.hover-effect--default;

      display: flex;
      align-items: center;
      justify-content: center;
      position: absolute;
      bottom: 0;
      background-color: rgba(var(--theme-color-grayscale--0-rgb), 0.6);
      color: variables.$va-color-grayscale--1000;
      font-size: variables.$font-size--small;
      width: 100%;
      text-align: center;
      height: 2.4rem;
      padding: 0.4rem;
      transition:
        height $button-show-more-transition,
        font-size $button-show-more-transition;
      transition-delay: 50ms;
      opacity: 1;
      gap: 0.5rem;
      z-index: 2;

      &--hide {
        opacity: 0;
      }

      &--is-expanded {
        bottom: -2rem;
        height: 2rem;
        font-size: variables.$font-size--smallest;
      }
    }
  }
</style>
